/**
 * Attribution bridge for AR community → MP hosted checkout.
 * External MP navigation often breaks PostHog session continuity; we stash
 * geo + mux + VIP href provenance before redirect and reattach on success.
 */

import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";

export const COMUNIDAD_AR_ATTRIBUTION_SS_KEY = "comunidad_ar_mp_attribution_v1";

export type VipHrefSource =
  | "geo_community"
  | "country_template"
  | "premium_invite_config"
  | "env_mp"
  | "none";

export type ComunidadArAttribution = {
  ts: number;
  funnel: "comunidad_ar";
  site: string;
  landing_path: string;
  vip_href_source: VipHrefSource;
  cta_destination?: string;
  playback_id?: string;
  playback_id_source?: string;
  dual_cta_geo_source?: string;
  dual_cta_session_id?: string;
  worker_country?: string;
  worker_province?: string;
  worker_city?: string;
  worker_neighborhood?: string;
};

export function resolveVipHrefSource(opts: {
  geoCommunityVipHref?: string | null;
  countryVipHref?: string | null;
  premiumInviteVipBase?: string | null;
  primaryHref?: string | null;
}): VipHrefSource {
  const geo = opts.geoCommunityVipHref?.trim() || "";
  if (geo) return "geo_community";
  const country = opts.countryVipHref?.trim() || "";
  if (country) return "country_template";
  const premium = opts.premiumInviteVipBase?.trim() || "";
  if (premium) return "premium_invite_config";
  const primary = opts.primaryHref?.trim() || "";
  if (primary && /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(primary)) {
    return "env_mp";
  }
  return "none";
}

export function persistComunidadArAttribution(
  partial: Omit<ComunidadArAttribution, "ts" | "funnel" | "site" | "worker_country" | "worker_province" | "worker_city" | "worker_neighborhood"> &
    Partial<Pick<ComunidadArAttribution, "worker_country" | "worker_province" | "worker_city" | "worker_neighborhood">>,
): void {
  if (typeof window === "undefined") return;
  const geo = getWorkerGeoAnalyticsProps();
  const payload: ComunidadArAttribution = {
    ts: Date.now(),
    funnel: "comunidad_ar",
    site: BRAND_SOURCE_KEY,
    landing_path: partial.landing_path,
    vip_href_source: partial.vip_href_source,
    cta_destination: partial.cta_destination,
    playback_id: partial.playback_id,
    playback_id_source: partial.playback_id_source,
    dual_cta_geo_source: partial.dual_cta_geo_source,
    dual_cta_session_id: partial.dual_cta_session_id,
    worker_country:
      partial.worker_country ||
      (typeof geo.worker_country === "string" ? geo.worker_country : undefined),
    worker_province:
      partial.worker_province ||
      (typeof geo.worker_province === "string" ? geo.worker_province : undefined),
    worker_city:
      partial.worker_city ||
      (typeof geo.worker_city === "string" ? geo.worker_city : undefined),
    worker_neighborhood:
      partial.worker_neighborhood ||
      (typeof geo.worker_neighborhood === "string"
        ? geo.worker_neighborhood
        : undefined),
  };
  try {
    sessionStorage.setItem(COMUNIDAD_AR_ATTRIBUTION_SS_KEY, JSON.stringify(payload));
  } catch {
    /* private mode */
  }
}

export function readComunidadArAttribution(): ComunidadArAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(COMUNIDAD_AR_ATTRIBUTION_SS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ComunidadArAttribution;
    if (!parsed || typeof parsed !== "object") return null;
    // Discard after 6h — stale geo from prior visits.
    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > 6 * 60 * 60 * 1000) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function comunidadArAttributionProps(
  attr: ComunidadArAttribution | null | undefined,
): Record<string, unknown> {
  if (!attr) return {};
  return {
    site: attr.site || BRAND_SOURCE_KEY,
    funnel: attr.funnel || "comunidad_ar",
    vip_href_source: attr.vip_href_source,
    ...(attr.landing_path ? { landing_path: attr.landing_path } : {}),
    ...(attr.cta_destination ? { cta_destination: attr.cta_destination } : {}),
    ...(attr.playback_id ? { playback_id: attr.playback_id } : {}),
    ...(attr.playback_id_source ? { playback_id_source: attr.playback_id_source } : {}),
    ...(attr.dual_cta_geo_source ? { dual_cta_geo_source: attr.dual_cta_geo_source } : {}),
    ...(attr.dual_cta_session_id ? { dual_cta_session_id: attr.dual_cta_session_id } : {}),
    ...(attr.worker_country ? { worker_country: attr.worker_country } : {}),
    ...(attr.worker_province ? { worker_province: attr.worker_province } : {}),
    ...(attr.worker_city ? { worker_city: attr.worker_city } : {}),
    ...(attr.worker_neighborhood
      ? { worker_neighborhood: attr.worker_neighborhood }
      : {}),
  };
}
